import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TextField,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import CalculateIcon from '@mui/icons-material/Calculate';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Description, PictureAsPdf, Refresh } from '@mui/icons-material';
import { useObtenerFiltrosVentas } from '@/shared/actions/erp';
import { useCalcularComisiones, useFetchReporteLider, useExportarComisiones, useFetchTaskStatus } from '@/shared/actions/erp/comisiones/comisiones.actions';
import type { FiltrosComisionAsesor, ComisionLiderRow } from '@/lib/commissions/types';
import { usePeriodo } from '../context/PeriodoContext';
import PeriodoIndicator from './PeriodoIndicator';

interface TabLiderProps {
  onOpenParametrizacion: () => void;
}

export default function TabLider({ onOpenParametrizacion }: TabLiderProps) {
  const {
    fechaInicio: periodoInicio,
    fechaFin: periodoFin,
    currentRunId,
    setCurrentRunId,
    taskId,
    setTaskId,
    isCalculating,
    setIsCalculating,
    canalesCalculados,
    setCanalesCalculados,
  } = usePeriodo();

  const [filtros, setFiltros] = useState<FiltrosComisionAsesor>({
    fecha_inicio: periodoInicio,
    fecha_fin: periodoFin,
    canales: [],
  });
  const [canalSearch, setCanalSearch] = useState('');

  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'xlsx' | 'pdf' | ''>('');
  const [exportProgress, setExportProgress] = useState(0);

  // Sincronizar filtros con el contexto de período
  useEffect(() => {
    setFiltros(prev => ({
      ...prev,
      fecha_inicio: periodoInicio,
      fecha_fin: periodoFin,
    }));
  }, [periodoInicio, periodoFin]);

  const { data: filtrosData, isLoading: isLoadingFiltros, refetch: refetchFiltros } = useObtenerFiltrosVentas();
  const calcularMutation = useCalcularComisiones({ disableGlobalLoading: true });
  const { data: resultados, isLoading: isLoadingResultados } = useFetchReporteLider(
    currentRunId,
    !!currentRunId
  );
  const exportarMutation = useExportarComisiones();

  // Polling para el estado de la tarea de Celery
  const { data: taskStatus } = useFetchTaskStatus(taskId, isCalculating && !!taskId);

  // Actualizar progreso y estado cuando cambia el taskStatus
  useEffect(() => {
    if (!taskStatus) return;

    const state = taskStatus.state;

    // Si la tarea terminó exitosamente
    if (state === 'SUCCESS' && taskStatus.run_id) {
      setCurrentRunId(taskStatus.run_id);
      setTimeout(() => {
        setIsCalculating(false);
        setTaskId(null);
      }, 500);
    }

    // Si la tarea falló
    if (state === 'FAILURE') {
      setIsCalculating(false);
      setTaskId(null);
    }
  }, [taskStatus, setCurrentRunId, setIsCalculating, setTaskId]);

  // Obtener lista de canales disponibles desde el endpoint (array de strings)
  // Filtrar valores nulos o vacíos
  const canalesDisponibles = useMemo(() => {
    if (!filtrosData?.data) return [];
    try {
      const canales = (filtrosData.data as any).canales || [];
      return canales.filter((canal: any) => canal != null && canal !== '');
    } catch (error) {
      return [];
    }
  }, [filtrosData]);

  // Filtrar canales por búsqueda
  const canalesFiltrados = useMemo(() => {
    if (!canalSearch) return canalesDisponibles;
    return canalesDisponibles.filter((canal: string) =>
      canal.toLowerCase().includes(canalSearch.toLowerCase())
    );
  }, [canalesDisponibles, canalSearch]);

  const selectedCanales = filtros.canales ?? [];

  const handleClearDates = () => {
    setFiltros((prev) => ({
      ...prev,
      fecha_inicio: periodoInicio,
      fecha_fin: periodoFin,
    }));
  };

  const handleChangeCanales = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    if (value.includes('SELECCIONAR_TODOS')) {
      setFiltros((prev) => ({
        ...prev,
        canales: selectedCanales.length === canalesDisponibles.length ? [] : canalesDisponibles,
      }));
    } else {
      setFiltros((prev) => ({
        ...prev,
        canales: value,
      }));
    }
  };

  const handleRefreshFiltros = async () => {
    await refetchFiltros();
  };

  const handleCalcular = async () => {
    // Si ya hay un cálculo en progreso, no hacer nada
    if (isCalculating) return;

    setIsCalculating(true);

    // Guardar los canales usados en este cálculo en el contexto compartido
    setCanalesCalculados(filtros.canales || []);

    try {
      // Llamar al backend que ahora devuelve task_id
      const result = await calcularMutation.mutateAsync(filtros);

      // result ahora contiene { task_id, status, message }
      if (result && result.task_id) {
        setTaskId(result.task_id);
        // El polling se encargará del resto a través del useEffect
      } else {
        // Fallback: si todavía viene el formato antiguo
        if (result && result.run && result.run.id) {
          setCurrentRunId(result.run.id);
          setTimeout(() => {
            setIsCalculating(false);
          }, 500);
        }
      }
    } catch (error) {
      setIsCalculating(false);
      setTaskId(null);
    }
  };

  const handleExportar = async (formato: 'xlsx' | 'pdf') => {
    if (!currentRunId) return;

    setIsExporting(true);
    setExportType(formato);
    setExportProgress(0);

    // Simular progreso
    const progressInterval = setInterval(() => {
      setExportProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      await exportarMutation.mutateAsync({
        run_id: currentRunId,
        formato,
        incluir_lider: true
      });
      setExportProgress(100);

      // Mantener el modal un momento más para mostrar 100%
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
        setExportType('');
      }, 500);
    } catch (error) {
      setIsExporting(false);
      setExportProgress(0);
      setExportType('');
    } finally {
      clearInterval(progressInterval);
    }
  };

  return (
    <Box>
      {/* Indicador de Período */}
      <PeriodoIndicator onOpenParametrizacion={onOpenParametrizacion} tipoComision="Líder" />

      {/* Modal de Calculo */}
      <Dialog
        open={isCalculating}
        maxWidth="sm"
        fullWidth
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setIsCalculating(false);
        }}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1, pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CalculateIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Calculando Comisiones
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Aplicando reglas de comisión y calculando totales
            </Typography>

            <Box sx={{ width: '100%' }}>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: 'primary.50',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                  },
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Este proceso puede tardar unos momentos...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de Exportación */}
      <Dialog
        open={isExporting}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            minWidth: 400,
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {exportType === 'xlsx' ? (
              <Description sx={{ fontSize: 48, color: 'success.main' }} />
            ) : (
              <PictureAsPdf sx={{ fontSize: 48, color: 'error.main' }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Exportando {exportType === 'xlsx' ? 'Excel' : 'PDF'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {exportProgress < 50 ? 'Preparando datos...' :
                 exportProgress < 80 ? 'Generando archivo...' :
                 'Finalizando descarga...'}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {exportProgress}% completado
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              Por favor espere, esto puede tomar unos segundos...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Filtros de Búsqueda
          </Typography>

          {/* Alert de error en carga de filtros */}
          {!isLoadingFiltros && canalesDisponibles.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                No se pudieron cargar los canales de venta. Por favor, intenta recargar usando el botón de actualización.
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Date Range Picker - OCULTO: Se usa el período configurado en Períodos */}
            {/* <Grid item xs={12} sm={6} md={4} lg={3}>
              <DateRangePicker
                size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                flatpickrRef={flatpickrRef}
                onChange={handleChangeRangeDate}
                defaultValue={`${filtros.fecha_inicio} to ${filtros.fecha_fin}`}
                label="Rango de Fechas"
              />
            </Grid> */}

            {/* Canales de Venta */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="canales-venta-label">Canales de Venta</InputLabel>
                <Select
                  labelId="canales-venta-label"
                  multiple
                  value={selectedCanales}
                  onChange={handleChangeCanales}
                  input={<OutlinedInput label="Canales de Venta" />}
                  disabled={isLoadingFiltros || canalesDisponibles.length === 0}
                  renderValue={(selected) =>
                    selected.length === canalesDisponibles.length ? 'Todos' : selected.join(', ')
                  }
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 300 },
                    },
                  }}
                  onClose={() => setCanalSearch('')}
                >
                  <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Buscar canal..."
                      fullWidth
                      value={canalSearch}
                      onChange={(e) => setCanalSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                  <MenuItem value="SELECCIONAR_TODOS" dense>
                    <Checkbox checked={selectedCanales.length === canalesDisponibles.length} size="small" />
                    <ListItemText primary="Seleccionar todos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  </MenuItem>
                  {canalesFiltrados.length > 0 ? (
                    canalesFiltrados.map((canal: string) => (
                      <MenuItem key={canal} value={canal} dense>
                        <Checkbox checked={selectedCanales.indexOf(canal) > -1} size="small" />
                        <ListItemText primary={canal} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled dense>
                      <ListItemText primary="No se encontraron canales" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Botón Recargar Canales */}
            <Grid item xs={12} sm={6} md={1} lg="auto">
              <Tooltip title="Recargar Canales">
                <IconButton
                  color="primary"
                  onClick={handleRefreshFiltros}
                  disabled={isLoadingFiltros}
                  sx={{
                    height: '40px',
                    width: '40px',
                    border: 1,
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.light',
                      borderColor: 'primary.dark'
                    },
                    '&.Mui-disabled': {
                      borderColor: 'action.disabled'
                    }
                  }}
                >
                  {isLoadingFiltros ? <CircularProgress size={20} /> : <Refresh fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Grid>

            {/* Divider */}
            <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
              <Divider orientation="vertical" flexItem sx={{ height: '40px', borderRightWidth: 2 }} />
            </Grid>

            {/* Botón Calcular Comisiones */}
            <Grid item xs={12} sm="auto" md="auto">
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<CalculateIcon />}
                onClick={handleCalcular}
                disabled={calcularMutation.isPending || isLoadingFiltros}
                sx={{
                  minWidth: { xs: 'auto', sm: 140 },
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4 },
                  height: '40px'
                }}
              >
                Calcular
              </Button>
            </Grid>

            {/* Botón Limpiar Filtros */}
            <Grid item xs={12} sm="auto" md="auto">
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                startIcon={<Refresh />}
                onClick={() => {
                  handleClearDates();
                  setFiltros((prev) => ({
                    ...prev,
                    canales: canalesDisponibles,
                  }));
                }}
                sx={{
                  minWidth: { xs: 'auto', sm: 140 },
                  textTransform: 'none',
                  fontWeight: 600,
                  height: '40px'
                }}
              >
                Limpiar Filtros
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Banner de recalcular */}
      {currentRunId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            ✓ Datos compartidos - Período {filtros.fecha_inicio} - {filtros.fecha_fin}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Canales de venta:</strong> {canalesCalculados && canalesCalculados.length > 0 ? canalesCalculados.join(', ') : 'Todos'}
          </Typography>
          <Typography variant="body2">
            Estos resultados están sincronizados con los tabs de Asesor y Jefatura.
            Si cambias los parámetros en la configuración, debes recalcular para aplicar los cambios.
          </Typography>
        </Alert>
      )}

      {/* Tabla de Resultados */}
      {resultados && resultados.length > 0 && (
        <Card elevation={2}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Resultados Líder
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {resultados.length} {resultados.length === 1 ? 'líder encontrado' : 'líderes encontrados'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handleExportar('xlsx')}
                  disabled={exportarMutation.isPending}
                  size="small"
                  sx={{
                    minWidth: '140px',
                    '&:hover': {
                      bgcolor: 'success.light',
                      color: 'white'
                    }
                  }}
                >
                  Excel
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handleExportar('pdf')}
                  disabled={exportarMutation.isPending}
                  size="small"
                  sx={{
                    minWidth: '140px',
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'white'
                    }
                  }}
                >
                  PDF
                </Button>
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
                maxHeight: '600px'
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Comisión Líderes</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Contratos</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Bono</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Ventas</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Capacitación</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>Total a Pagar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultados.map((row: ComisionLiderRow, idx: number) => (
                    <TableRow
                      key={idx}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        '&:last-child td': { borderBottom: 0 }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.nombre}</TableCell>
                      <TableCell align="right">
                        <Box
                          component="span"
                          sx={{
                            bgcolor: 'primary.light',
                            color: 'primary.dark',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        >
                          {row.total_contratos_global}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          component="span"
                          sx={{
                            bgcolor: 'info.light',
                            color: 'info.dark',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        >
                          {(Number(row.factor_aplicado) * 100).toFixed(0)}%
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box component="span" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                          ${Number(row.ventas_fijo).toFixed(2)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box component="span" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                          ${Number(row.capacitacion_fijo).toFixed(2)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          component="span"
                          sx={{
                            bgcolor: 'success.light',
                            color: 'black',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1,
                            fontWeight: 700,
                            fontSize: '0.9rem'
                          }}
                        >
                          ${Number(row.total_comision).toFixed(2)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Estado de carga */}
      {isLoadingResultados && (
        <Card elevation={1}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 2
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="h6" color="text.secondary">
                Cargando resultados...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por favor espere un momento
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sin resultados después de calcular */}
      {currentRunId && !isLoadingResultados && (!resultados || resultados.length === 0) && (
        <Card elevation={1}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 2
              }}
            >
              <Alert severity="warning" sx={{ mb: 2, maxWidth: 600 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  ⚠️ No se calcularon comisiones de líder
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  El cálculo se ejecutó correctamente pero no generó resultados de líder. Esto ocurre cuando:
                </Typography>
                <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                  <li><strong>La configuración de líder está inactiva</strong> (campo "activo" = False)</li>
                  <li><strong>No hay líderes configurados</strong> en los parámetros</li>
                  <li><strong>Los tramos de líder no están configurados</strong></li>
                </ul>
                <Typography variant="body2" sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                  💡 <strong>Solución:</strong> Ve al drawer de "Parametrización" → pestaña "Líder" y verifica que:
                  <br />
                  1. El switch "Activo" esté en ON
                  <br />
                  2. Haya al menos un líder configurado y activo
                  <br />
                  3. Existan tramos configurados con rangos de contratos y factores
                </Typography>
              </Alert>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sin resultados antes de calcular */}
      {!currentRunId && !isLoadingResultados && (
        <Card elevation={1}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 2
              }}
            >
              <CalculateIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="h6" color="text.secondary">
                No hay resultados para mostrar para Líder
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                Selecciona canales y luego haz clic en "Calcular" para generar el reporte.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
